import React from 'react'
import { Text, LoadingSpinner } from './ui'
import { comercialGestaoService } from '../services/comercialGestao'
import { getClassificationCardStyle, normalizeClassificationKey } from '../utils/classificationTrend'
import type { BrokerClassificationKpi } from '../types/comercial'

const CLASSIFICATION_ICON_BY_NAME: Record<string, string> = {
  BASIC: '/basic.png',
  BRONZE: '/bronze.png',
  SILVER: '/silver.png',
  START: '/start.png',
  GOLD: '/gold.png',
  PLATINUM: '/platinum.png',
}

function getClassificationIcon(name: string): string | undefined {
  return CLASSIFICATION_ICON_BY_NAME[normalizeClassificationKey(name)]
}

interface ClassificationKpiCardProps {
  kpi: BrokerClassificationKpi
}

const ClassificationKpiCard: React.FC<ClassificationKpiCardProps> = ({ kpi }) => {
  const custom = getClassificationCardStyle(kpi.color)
  const iconSrc = getClassificationIcon(kpi.name)
  const showWithProduction =
    kpi.name.toUpperCase() === 'START' && kpi.with_production_count != null
  const isGradient = Boolean(custom)

  const cardShell = isGradient
    ? 'relative overflow-hidden flex h-full min-w-[7.5rem] flex-col items-center rounded-2xl text-white shadow-lg ring-1 ring-white/25'
    : 'relative overflow-hidden flex h-full min-w-[7.5rem] flex-col items-center rounded-2xl border border-gray-200 bg-white text-gray-900 shadow dark:border-gray-700 dark:bg-gray-800 dark:text-white'

  const medallion = isGradient
    ? 'relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-white/15 shadow-inner ring-2 ring-white/30 backdrop-blur-sm'
    : 'relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 shadow-inner ring-2 ring-gray-200/80 dark:bg-gray-700/50 dark:ring-gray-600'

  const title = isGradient
    ? 'relative z-10 mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/90'
    : 'relative z-10 mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400'

  const dock = isGradient
    ? 'relative z-10 mt-3 w-full rounded-xl bg-black/15 p-2 ring-1 ring-white/15 backdrop-blur-[2px]'
    : 'relative z-10 mt-3 w-full rounded-xl bg-gray-50 p-2 ring-1 ring-gray-200/80 dark:bg-gray-900/50 dark:ring-gray-700'

  const statLabel = isGradient
    ? 'text-[9px] font-semibold uppercase tracking-wide text-white/70'
    : 'text-[9px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'

  const statValue = isGradient
    ? 'text-lg font-bold tabular-nums leading-none text-white'
    : 'text-lg font-bold tabular-nums leading-none text-gray-900 dark:text-white'

  const statValueSolo = isGradient
    ? 'text-2xl font-bold tabular-nums leading-none text-white'
    : 'text-2xl font-bold tabular-nums leading-none text-gray-900 dark:text-white'

  return (
    <article className={cardShell} style={custom}>
      {iconSrc && (
        <img
          src={iconSrc}
          alt=""
          className="pointer-events-none absolute left-1/2 top-6 h-24 w-24 -translate-x-1/2 object-contain opacity-[0.12] blur-[0.5px] select-none"
          aria-hidden
        />
      )}

      {isGradient && (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/30" aria-hidden />
          <div
            className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-2xl"
            aria-hidden
          />
        </>
      )}

      <div className="flex h-full w-full flex-col items-center px-3 pb-3 pt-4">
        <div className={medallion}>
          {iconSrc ? (
            <img src={iconSrc} alt="" className="h-9 w-9 object-contain drop-shadow-sm" aria-hidden />
          ) : null}
        </div>

        <h3 className={title}>{kpi.name}</h3>

        <div className={`${dock} mt-auto min-h-[4.25rem]`}>
          {showWithProduction ? (
            <div
              className={`grid h-full min-h-[3.25rem] grid-cols-2 divide-x ${
                isGradient ? 'divide-white/15' : 'divide-gray-200 dark:divide-gray-700'
              }`}
            >
              <div className="flex flex-col items-center justify-center gap-1 px-1 text-center">
                <span className={statLabel}>C/ Prod.</span>
                <span className={statValue}>{kpi.with_production_count!.toLocaleString('pt-BR')}</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-1 px-1 text-center">
                <span className={statLabel}>Total</span>
                <span className={statValue}>{kpi.count.toLocaleString('pt-BR')}</span>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[3.25rem] flex-col items-center justify-center gap-1 text-center">
              <span className={statLabel}>Corretores</span>
              <span className={statValueSolo}>{kpi.count.toLocaleString('pt-BR')}</span>
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

interface ClassificationKpiCardsViewProps {
  kpis: BrokerClassificationKpi[]
  loading?: boolean
  error?: string | null
}

const ClassificationKpiCardsView: React.FC<ClassificationKpiCardsViewProps> = ({
  kpis,
  loading = false,
  error = null,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
        <Text size="sm">{error}</Text>
      </div>
    )
  }

  if (kpis.length === 0) return null

  return (
    <div
      className="grid w-full auto-cols-fr grid-flow-col items-stretch gap-3 overflow-x-auto pb-1"
      aria-label="Totais por categoria"
    >
      {kpis.map((kpi) => (
        <ClassificationKpiCard key={kpi.name} kpi={kpi} />
      ))}
    </div>
  )
}

export type ClassificationKpiCardsProps =
  | ({ standalone: true } & Partial<Pick<ClassificationKpiCardsViewProps, 'loading' | 'error'>>)
  | ({ standalone?: false } & ClassificationKpiCardsViewProps)

/** Cards de contagem por categoria. Em `standalone`, carrega totais globais uma vez e ignora filtros da página. */
export const ClassificationKpiCards: React.FC<ClassificationKpiCardsProps> = (props) => {
  const [kpis, setKpis] = React.useState<BrokerClassificationKpi[]>([])
  const [loading, setLoading] = React.useState(props.standalone === true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (props.standalone !== true) return

    let cancelled = false
    setLoading(true)
    setError(null)

    void comercialGestaoService
      .getBrokerCountsByClassificationKpis()
      .then((data) => {
        if (!cancelled) setKpis(data)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setKpis([])
          setError(err instanceof Error ? err.message : 'Erro ao carregar KPIs de categoria.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [props.standalone])

  if (props.standalone === true) {
    return <ClassificationKpiCardsView kpis={kpis} loading={loading} error={error} />
  }

  return (
    <ClassificationKpiCardsView
      kpis={props.kpis}
      loading={props.loading}
      error={props.error}
    />
  )
}
